/**
 * micro:bit 놀이터 1단계 — 별 모으기 판 · 무대 · 미션(PICNIC-MB-FIELD-001 §3 유형 T 무대 · R 리모컨 · §1 원칙 3).
 *
 * 순수 상태(DOM · 시간 없음 — 호출하는 쪽이 지난 시간 ms 를 넘긴다). 같은 입력이면 같은 결과다.
 *  - 판: 7×7 칸. 캐릭터는 리모컨 방향(보드 기울기)으로 MOVE_MS 마다 한 칸 · 별 3개를 모은다(모으면 새 별).
 *  - 무대: 보드 화면이 아이콘이 되면 관객이 그 아이콘에 반응한다(아이콘 이름 = 반응 열쇠).
 *  - 흔들기 = 점프(무대 박수 한 번) · A · B 를 누른 적 있는지 센다.
 *  - 미션 셋: ① 아이콘 3가지 보여 주기 ② A · B 둘 다 눌러 보기 ③ 기울여서 별 3개 모으기 — 놀이터가 본 사건으로만 판정한다.
 *    (Campus 로 결과를 보내는 서명 `missionResult` 는 다음 단계 — 지금은 화면에서 확인)
 */
import type { RemoteInput } from "@picnic-field/local-host/protocol";
import type { MbFrame } from "./live";
import { iconOf } from "./live";

export const GRID = 7;
export const MOVE_MS = 300;
export const STARS_GOAL = 3;
export const ICONS_GOAL = 3;

export interface Cell {
  readonly x: number;
  readonly y: number;
}

export interface PlayState {
  readonly me: Cell;
  readonly star: Cell;
  readonly stars: number;
  /** 다음 칸으로 가기까지 남은 시간(ms) — 기울기를 유지하면 MOVE_MS 마다 한 칸 */
  readonly wait: number;
  readonly icons: readonly string[];
  readonly lastIcon: string | null;
  readonly pressedA: boolean;
  readonly pressedB: boolean;
  readonly jumps: number;
  readonly wasShake: boolean;
  /** 별 자리 고르기 순서(결정론) */
  readonly seq: number;
}

export interface Goals {
  readonly icons: boolean;
  readonly buttons: boolean;
  readonly stars: boolean;
}

/** 별 자리 — 정해진 차례(난수 없음 · 리플레이 · 시험이 같다). 캐릭터 자리는 건너뛴다 */
const SPOTS: readonly Cell[] = [
  { x: 5, y: 1 }, { x: 1, y: 5 }, { x: 6, y: 6 }, { x: 0, y: 0 }, { x: 3, y: 6 }, { x: 6, y: 2 }, { x: 2, y: 1 }, { x: 4, y: 4 },
];

export function initialPlay(): PlayState {
  return { me: { x: 3, y: 3 }, star: SPOTS[0]!, stars: 0, wait: 0, icons: [], lastIcon: null, pressedA: false, pressedB: false, jumps: 0, wasShake: false, seq: 0 };
}

const STEP: Record<string, Cell> = {
  n: { x: 0, y: -1 }, s: { x: 0, y: 1 }, e: { x: 1, y: 0 }, w: { x: -1, y: 0 },
  ne: { x: 1, y: -1 }, nw: { x: -1, y: -1 }, se: { x: 1, y: 1 }, sw: { x: -1, y: 1 },
};

const clamp = (v: number) => Math.max(0, Math.min(GRID - 1, v));

/** 한 걸음 — 지난 시간(ms) · 리모컨 입력 · 보드 모습 */
export function stepPlay(s: PlayState, dtMs: number, input: RemoteInput, frame: MbFrame | null): PlayState {
  let { me, star, stars, wait, seq } = s;
  // 움직임 — 방향이 있으면 남은 시간이 다 될 때마다 한 칸(처음 기울인 순간에 바로 한 칸)
  if (input.dpad) {
    wait -= dtMs;
    if (wait <= 0) {
      const d = STEP[input.dpad]!;
      me = { x: clamp(me.x + d.x), y: clamp(me.y + d.y) };
      wait += MOVE_MS;
      if (wait < 0) wait = 0;
    }
  } else wait = 0;
  if (me.x === star.x && me.y === star.y) {
    stars += 1;
    seq += 1;
    let next = SPOTS[seq % SPOTS.length]!;
    if (next.x === me.x && next.y === me.y) { seq += 1; next = SPOTS[seq % SPOTS.length]!; }
    star = next;
  }
  // 무대 — 아이콘
  const icon = frame ? iconOf(frame.leds) : null;
  const icons = icon && !s.icons.includes(icon) ? [...s.icons, icon] : s.icons;
  // 흔들기 = 점프(올라가는 순간 한 번)
  const shake = !!frame?.shake;
  const jumps = shake && !s.wasShake ? s.jumps + 1 : s.jumps;
  return {
    me, star, stars, wait, seq, icons,
    lastIcon: icon ?? (frame ? null : s.lastIcon),
    pressedA: s.pressedA || input.a,
    pressedB: s.pressedB || input.b,
    jumps, wasShake: shake,
  };
}

export function goalsOf(s: PlayState): Goals {
  return { icons: s.icons.length >= ICONS_GOAL, buttons: s.pressedA && s.pressedB, stars: s.stars >= STARS_GOAL };
}

/** 관객 반응 — 아이콘마다 관객이 보이는 이모지(없으면 기본 박수) */
export const REACTION: Readonly<Record<string, string>> = {
  Heart: "💖", SmallHeart: "💗", Happy: "😄", Sad: "🥺", Angry: "😤", Surprised: "😲", Asleep: "😴", Silly: "🤪", Fabulous: "🤩",
  Confused: "🤔", Meh: "😐", Yes: "🙆", No: "🙅", Duck: "🦆", Rabbit: "🐰", Cow: "🐮", Tortoise: "🐢", Butterfly: "🦋", Giraffe: "🦒",
  Snake: "🐍", Ghost: "👻", House: "🏠", Umbrella: "☂️", QuarterNote: "🎵", EighthNote: "🎶", Diamond: "💎", SmallDiamond: "🔹",
};
export const reactionOf = (icon: string | null): string => (icon ? REACTION[icon] ?? "👏" : "👀");
